import { createContext, useContext } from 'react';

export type SelectableItem = string | Record<string, any>;

export type SelectOptionRegistration<T extends SelectableItem> = {
  value: T;
  isPlaceholder?: boolean;
  getNode: () => HTMLElement | null;
};

export type SelectContext<T extends SelectableItem> = {
  variant: 'select' | 'radio';
  value: T | null;
  values: T[] | null;
  onChange: (value: T, index?: number) => void;
  indexOfFocusedItem?: number;
  formatLabel: (value: T | null) => string;
  /*
   * Options announce themselves so keyboard navigation knows the rendered set even when a consumer
   * wraps them in its own component — `children` introspection cannot see through that.
   */
  registerOption?: (key: object, registration: SelectOptionRegistration<T>) => () => void;
};

const Context = createContext<SelectContext<any> | null>(null);

export const useSelectContext = <T extends SelectableItem>(): SelectContext<T> => {
  const value = useContext(Context);
  if (!value) throw new Error('SelectContext should not be null');
  return value as SelectContext<T>;
};

export const SelectProvider = Context.Provider;
